import { useState, useCallback } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { TableRows } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ZipfilesGrid from '../components/ZipfilesGrid';
import FMOInputsPanel from '../panels/FMOInputsPanel';

export default function CenterContent({
  // Grid props
  zipfiles,
  loading,
  error,
  selectedZipfile,
  onZipfileSelect,
  onRefresh,
  totalCount,
  treeData,  // Filtered tree data for hierarchical display
  
  // FMO Inputs panel props
  bottomPanelOpen,
  setBottomPanelOpen,
  bottomPanelHeight,
  setBottomPanelHeight,
  fmoInputs,
  fmoInputsLoading,
  fmoInputsError,
  selectedFMOInput,
  onFMOInputSelect,
  selectedNode,  // Browser selection for title context
}) {
  const theme = useTheme();
  const [isDraggingHandle, setIsDraggingHandle] = useState(false);
  
  // Compute the title for the Entries panel
  const getEntriesTitle = () => {
    // If a specific zipfile is selected, show its name
    if (selectedZipfile) {
      if (selectedZipfile.isSpecRow) {
        // For spec rows, find the parent name
        const parentRow = treeData.find(row => 
          row.id_ === selectedZipfile.parentId && row.isParentRow
        );
        const parentName = parentRow?.name || 'Archive';
        
        // Build spec description
        const specParts = [];
        if (selectedZipfile.clusterspec) specParts.push(selectedZipfile.clusterspec);
        if (selectedZipfile.pocketspec) specParts.push(selectedZipfile.pocketspec);
        if (selectedZipfile.inputspec) specParts.push(selectedZipfile.inputspec);
        
        return `${parentName} - ${specParts.join(', ')}`;
      }
      
      // For parent rows, use the name directly
      return selectedZipfile.name;
    }
    
    // If only Browser node is selected, show its context
    if (selectedNode) {
      if (selectedNode.type === 'project') {
        return `Project: ${selectedNode.name}`;
      } else if (selectedNode.type === 'target') {
        return `Target: ${selectedNode.project} / ${selectedNode.name}`;
      } else if (selectedNode.type === 'batch') {
        return `Batch: ${selectedNode.project} / ${selectedNode.target} / ${selectedNode.name}`;
      }
    }
    
    // No selection
    return 'No Selection';
  };
  
  // Handle panel resize
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    setIsDraggingHandle(true);
    const startY = e.clientY;
    const startHeight = bottomPanelHeight; // Already in pixels
    
    const handleMouseMove = (e) => {
      const deltaY = startY - e.clientY;
      const newHeightPx = startHeight + deltaY;
      const minHeight = 200; // Minimum height in pixels
      const maxHeight = window.innerHeight - 300; // Leave 300px for other UI elements
      const clampedHeight = Math.min(Math.max(newHeightPx, minHeight), maxHeight);
      setBottomPanelHeight(Math.round(clampedHeight));
    };
    
    const handleMouseUp = () => {
      setIsDraggingHandle(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [bottomPanelHeight, setBottomPanelHeight]);
  
  return (
    <Box 
      sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Main Content - dynamically sized based on FMO inputs panel */}
      <Box 
        sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          mb: bottomPanelOpen ? `${bottomPanelHeight + parseInt(theme.spacing(5))}px` : theme.spacing(5) // Dynamic margin
        }}
      >
        <ZipfilesGrid
          zipfiles={zipfiles}
          loading={loading}
          error={error}
          selectedZipfile={selectedZipfile}
          onZipfileSelect={onZipfileSelect}
          onRefresh={onRefresh}
          totalCount={totalCount}
          showLeftSidebar={false}
          treeData={treeData}  // Pass filtered tree data for display
          displayCount={treeData.length}  // Pass actual count of displayed rows
        />
      </Box>

      {/* Drag Handle for FMO Inputs Panel */}
      {bottomPanelOpen && (
        <Box
          onMouseDown={handleDragStart}
          sx={{
            position: 'absolute',
            bottom: bottomPanelHeight + parseInt(theme.spacing(5)) - parseInt(theme.spacing(0.125)), // Position handle just above panel top
            left: 0,
            right: 0,
            height: theme.spacing(0.125), // Thin resize handle that scales with density
            cursor: 'ns-resize',
            backgroundColor: isDraggingHandle ? 'primary.main' : 'divider',
            '&:hover': {
              backgroundColor: 'primary.main'
            },
            zIndex: 1100
          }}
        />
      )}
      
      {/* FMO Inputs Panel - positioned absolutely but pushes content up via margin */}
      {bottomPanelOpen && (
        <Box
          sx={{
            position: 'absolute',
            bottom: theme.spacing(5), // Above the toolbar
            left: 0,
            right: 0,
            height: `${bottomPanelHeight}px`,
            zIndex: 999,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <FMOInputsPanel
            fmoInputs={fmoInputs}
            loading={fmoInputsLoading}
            error={fmoInputsError}
            selectedFMOInput={selectedFMOInput}
            onFMOInputSelect={onFMOInputSelect}
            zipfileName={getEntriesTitle()}
            onClose={() => setBottomPanelOpen(false)}
            isBottomPanel={true}
          />
        </Box>
      )}

      {/* Fixed Bottom Toolbar - Always Visible */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: theme.spacing(5), // Match IconButton height
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
      >
        {bottomPanelOpen && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: theme.spacing(4), // 32px at standard density
              height: theme.spacing(0.375), // 3px at standard density
              bgcolor: 'primary.main',
            }}
          />
        )}
        <Box sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center'
        }}>
          <Tooltip 
            title={bottomPanelOpen ? 'Hide FMO Inputs Panel' : 'Show FMO Inputs Panel'} 
            placement="top"
          >
            <IconButton
              onClick={() => setBottomPanelOpen(!bottomPanelOpen)}
              sx={{
                width: theme.spacing(5),
                height: theme.spacing(5), // Full height of toolbar
                color: bottomPanelOpen ? 'primary.main' : 'text.secondary',
                bgcolor: bottomPanelOpen ? 'action.selected' : 'transparent',
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: 'action.hover',
                },
                '&:disabled': {
                  color: 'text.disabled',
                },
                borderRadius: 0,
                '& .MuiSvgIcon-root': {
                  fontSize: theme.dimensions.icon.medium,
                },
              }}
            >
              <TableRows />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}